import { useSelector } from 'react-redux';

import type { UIPublicAttribute } from 'src/adapters/storeToUI/adaptPublicAttributesToUI';
import { adaptPublicAttributesToUI } from 'src/adapters/storeToUI/adaptPublicAttributesToUI';
import { PARTNER_MODULE_ID } from 'src/components/layouts/PDP/elementIds';
import { getTopProductFeatures } from 'src/components/molecules/ProductFeatures/utils/getTopProductFeatures';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { selectAccessibility } from 'src/redux/accessibility/selectors';
import { selectOptions } from 'src/redux/basket/selectors';
import { selectBreadcrumbsLinks } from 'src/redux/breadcrumbs/selectors';
import { selectOfferLabelContent } from 'src/redux/content/selectors';
import { selectDeliveryZones } from 'src/redux/delivery/selectors';
import { selectPartnerInsights } from 'src/redux/partner/selectors';
import { selectProduct } from 'src/redux/product/selectors';
import { getOptionsCostRange } from 'src/redux/product/utils/getOptionsCostRange';
import { selectProductReviews } from 'src/redux/reviews/selectors';
import { isMadeToOrder } from 'src/utils/inventoryStatus';
import type { PDPViewModel } from './types';

export interface PDPLayoutData {
  deliveryOptionsLoaded: boolean;
  hasProductOptions: boolean;
  partnerMaxi: {
    /** Link to the partner's storefront page */
    linkHref: string;
    shortcode: string;
  };
  /** null = product has no public attributes */
  publicAttributes: UIPublicAttribute[] | null;
  similarProductsServingConfig: 'pdp_similar_products' | 'pdp-similar-products-bury-sale';
  viewModel: PDPViewModel;
}

export const usePDPViewModel = (): PDPLayoutData => {
  const product = useSelector(selectProduct);
  const breadcrumbs = useSelector(selectBreadcrumbsLinks);
  const reviews = useSelector(selectProductReviews);
  const options = useSelector(selectOptions);
  const accessibility = useSelector(selectAccessibility);
  const offerLabelContent = useSelector(selectOfferLabelContent);
  const partnerInsights = useSelector(selectPartnerInsights);
  const deliveryZones = useSelector(selectDeliveryZones);

  const optionsCostRange = getOptionsCostRange(options, product.price.currency);
  const optionsAddCost =
    options.length > 0 &&
    product.specialFeatures.personalisable &&
    optionsCostRange.minRequired > 0;
  const minimumOptionsCost = optionsAddCost ? optionsCostRange.minRequired : 0;
  const priceIsMinimum = optionsCostRange.maxOptional + optionsCostRange.maxRequired > 0;

  const offerLabelAllowedForProduct =
    !offerLabelContent?.showOnlyForDiscountedProducts || product.onSale;
  const offerLabel =
    offerLabelContent && offerLabelAllowedForProduct
      ? {
          countdown: offerLabelContent.countdown,
          message: offerLabelContent.message,
          promoCode: offerLabelContent.promoCode,
          showInRed: offerLabelContent.showInRed,
        }
      : null;

  const productFeatures = getTopProductFeatures({
    allowsGiftWrap: product.allowsGiftWrap,
    productRating: reviews.averageProductRating,
    specialFeatures: product.specialFeatures,
  });

  return {
    deliveryOptionsLoaded: Boolean(deliveryZones),
    hasProductOptions: options.length > 0,
    partnerMaxi: {
      linkHref: `/partners/${product.partner.shortcode}`,
      shortcode: product.partner.shortcode,
    },
    publicAttributes: product.publicAttributes
      ? adaptPublicAttributesToUI(product.publicAttributes)
      : null,
    similarProductsServingConfig: product.onSale
      ? 'pdp_similar_products'
      : 'pdp-similar-products-bury-sale',
    viewModel: {
      badge: product.attributes?.highlyRated ? 'mostLoved' : product.isNew ? 'new' : undefined,
      breadcrumbs,
      offerLabel,
      pageLiveRegionText: accessibility.statusMessage,
      partner: {
        linkHref: `#${PARTNER_MODULE_ID}`,
        location: product.partner.location,
        logoSrc: product.partner.logo,
        name: product.partner.name,
      },
      partnerReviews: {
        count: partnerInsights?.numberOfPartnerReviews ?? undefined,
        rating: partnerInsights?.averageProductRating ?? undefined,
      },
      price: {
        minimumOptionsCost,
        preSalePrices: product.preSalePrices,
        price: product.price,
        priceIsMinimum,
        salePercentage: product.salePercentage || 0,
        salePercentageInRed: Boolean(offerLabel?.showInRed),
      },
      productFeatures,
      productInformation: {
        dimensions: product.dimensions || undefined,
        ingredientsFileURLPath: product.ingredients ? product.ingredients.rel! : undefined,
        materials: product.materials || undefined,
        otherInformation: product.otherInformation || '',
        productCode: product.code,
        variationsAvailable: product.variationsAvailable || undefined,
      },
      productReviews: {
        count: reviews.numberOfReviews || 0,
        href: `#${REVIEWS_URL_FRAGMENT_ID}`,
        rating: reviews.averageProductRating || null,
      },
      showMadeToOrder: isMadeToOrder(product.inventoryStatus),
      title: product.title,
      description: product.description,
    },
  };
};
